import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import useGetCountries from './useGetCountries';

interface CountryAlpha3Option {
  value: string;
  label: string;
}

interface CountryAlpha3OptionsResult {
  options: CountryAlpha3Option[];
  isLoading: boolean;
  alpha3ToAlpha2: (alpha3: string | undefined | null) => string | undefined;
  alpha2ToAlpha3: (alpha2: string | undefined | null) => string | undefined;
}

/**
 * Provides localized alpha-3 country select options from the backend countries API.
 *
 * Also exposes bidirectional alpha-3 ↔ alpha-2 mapping functions derived from
 * the same dataset (used by Google Places address autocomplete).
 */
export default function useCountryAlpha3Options(): CountryAlpha3OptionsResult {
  const { data: countries, isLoading } = useGetCountries();
  const { i18n } = useTranslation();
  const lang = i18n.language?.slice(0, 2);

  const { options, a3ToA2, a2ToA3 } = useMemo(() => {
    const countriesWithAlpha3 = countries?.filter((c) => c.alpha3Code);

    if (!countriesWithAlpha3?.length) {
      return {
        options: [] as CountryAlpha3Option[],
        a3ToA2: new Map<string, string>(),
        a2ToA3: new Map<string, string>(),
      };
    }

    const opts: CountryAlpha3Option[] = countriesWithAlpha3
      .map((c) => ({
        value: c.alpha3Code!,
        label: c.translations?.[lang] ?? c.name,
      }))
      .sort((a, b) => a.label.localeCompare(b.label, lang));

    const alpha3ToAlpha2Map = new Map<string, string>();
    const alpha2ToAlpha3Map = new Map<string, string>();

    for (const c of countriesWithAlpha3) {
      const a3 = c.alpha3Code!.toUpperCase();
      const a2 = c.alpha2Code.toLowerCase();
      alpha3ToAlpha2Map.set(a3, a2);
      alpha2ToAlpha3Map.set(c.alpha2Code.toUpperCase(), a3);
    }

    return {
      options: opts,
      a3ToA2: alpha3ToAlpha2Map,
      a2ToA3: alpha2ToAlpha3Map,
    };
  }, [countries, lang]);

  const alpha3ToAlpha2 = useMemo(
    () =>
      (alpha3: string | undefined | null): string | undefined => {
        if (alpha3 == null || alpha3 === '') return undefined;
        return a3ToA2.get(alpha3.trim().toUpperCase());
      },
    [a3ToA2],
  );

  const alpha2ToAlpha3 = useMemo(
    () =>
      (alpha2: string | undefined | null): string | undefined => {
        if (alpha2 == null || alpha2 === '') return undefined;
        return a2ToA3.get(alpha2.trim().toUpperCase());
      },
    [a2ToA3],
  );

  return { options, isLoading, alpha3ToAlpha2, alpha2ToAlpha3 };
}
